"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { useState, Fragment } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Logo } from "@/components/brand/logo";
import {
  LayoutDashboard,
  Calculator,
  FileText,
  Shield,
  KeyRound,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useHasResourceAccess, useIsMasterCompany } from "@/hooks/use-permissions";

interface MenuItem {
  label: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: MenuItem[];
  resource?: string; // Nome do recurso para verificação de permissão
  topamaisOnly?: boolean;
}

const menuItems: MenuItem[] = [
  {
    label: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    resource: "dashboard",
  },
  {
    label: "Simulação",
    href: "/simulacao",
    icon: Calculator,
    resource: "simulacao",
  },
  {
    label: "Propostas",
    href: "/propostas",
    icon: FileText,
    resource: "propostas",
  },
  {
    label: "Acessos",
    href: "/acessos/hierarquia",
    icon: Shield,
    resource: "acessos",
  },
  {
    label: "API Keys",
    href: "/configuracoes/api-keys",
    icon: KeyRound,
    topamaisOnly: true,
  },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

async function refreshProposalsCache(queryClient: QueryClient) {
  await queryClient.cancelQueries({ queryKey: ["proposals"] });
  queryClient.removeQueries({ queryKey: ["proposals"], type: "inactive" });
  await queryClient.invalidateQueries({ queryKey: ["proposals"] });
  await queryClient.refetchQueries({ queryKey: ["proposals"], type: "active" });
}

// Componente para item filho do menu com verificação de permissão
function MenuChildItem({
  child,
  onClose,
}: Readonly<{ child: MenuItem; onClose?: () => void }>) {
  const pathname = usePathname();
  const hasResourceAccess = useHasResourceAccess(child.resource ?? "");
  const hasAccess = child.resource ? hasResourceAccess : true;

  if (!hasAccess) {
    return null;
  }

  if (!child.href) {
    return null;
  }

  const ChildIcon = child.icon;
  const childIsActive = child.href
    ? pathname === child.href || pathname?.startsWith(`${child.href}/`)
    : false;

  return (
    <li>
      <Link
        href={child.href ?? "#"}
        onClick={() => {
          if (window.innerWidth < 1024 && onClose) {
            onClose();
          }
        }}
        aria-current={childIsActive ? "page" : undefined}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-150",
          childIsActive
            ? "text-primary border-primary border-l-4 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
            : "border-l-4 border-transparent text-[#2B2B2B] hover:bg-white/60",
        )}
      >
        <ChildIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span className="tracking-tight">{child.label}</span>
      </Link>
    </li>
  );
}

// Componente para item de menu com verificação de permissão
function MenuItemWithPermission({
  item,
  onClose,
  onNavigate,
}: Readonly<{
  item: MenuItem;
  onClose?: () => void;
  onNavigate?: (href?: string) => Promise<void>;
}>) {
  const pathname = usePathname();
  const [isExpanded, setIsExpanded] = useState(false);

  const hasChildren = item.children && item.children.length > 0;

  // Verificar permissão do item pai
  const hasResourceAccess = useHasResourceAccess(item.resource ?? "");
  const hasParentAccess = item.resource ? hasResourceAccess : true;

  // Verificar permissões de todos os filhos
  const childrenAccess =
    hasChildren && item.children
      ? item.children.map((child) => useHasResourceAccess(child.resource ?? ""))
      : [];

  // Verificar se pelo menos um filho tem acesso
  const hasAtLeastOneChildAccess = hasChildren
    ? childrenAccess.some(Boolean)
    : false;

  const shouldShow = hasChildren ? hasAtLeastOneChildAccess : hasParentAccess;

  if (!shouldShow) {
    return null;
  }

  const Icon = item.icon;
  const isActive = item.href
    ? pathname === item.href || pathname?.startsWith(`${item.href}/`)
    : false;

  // Verificar se algum filho está ativo
  const hasActiveChild =
    hasChildren && item.children
      ? item.children.some(
          (child) => child.href && pathname?.startsWith(child.href),
        )
      : false;

  return (
    <li>
      {item.href ? (
        <Link
          href={item.href}
          onClick={async (event) => {
            if (item.href === "/propostas") {
              event.preventDefault();
            }
            await onNavigate?.(item.href);
            if (window.innerWidth < 1024 && onClose) {
              onClose();
            }
          }}
          aria-current={isActive ? "page" : undefined}
          className={cn(
            "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-all duration-150",
            isActive
              ? "text-primary border-primary border-l-4 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
              : "border-l-4 border-transparent text-[#1a1a1a] hover:bg-white/60",
          )}
        >
          <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <span className="flex-1 tracking-tight">{item.label}</span>
        </Link>
      ) : (
        <>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            aria-haspopup="menu"
            className={cn(
              "group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-all duration-150",
              hasActiveChild || isExpanded
                ? "bg-white/40 text-[#1a1a1a]"
                : "text-[#1a1a1a] hover:bg-white/60",
            )}
            type="button"
          >
            <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            <span className="flex-1 text-left tracking-tight">
              {item.label}
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 opacity-60" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-4 w-4 opacity-60" aria-hidden="true" />
            )}
          </button>

          {/* Submenu Inline (Acordeão) */}
          {isExpanded && hasChildren && item.children && (
            <ul
              className="mt-1 space-y-0.5 rounded-lg bg-white/15 p-1 pl-4"
              role="menu"
            >
              {item.children.map((child) => (
                <MenuChildItem
                  key={child.label}
                  child={child}
                  onClose={onClose}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  );
}

export function Sidebar({ isOpen = false, onClose }: Readonly<SidebarProps>) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const isMasterCompany = useIsMasterCompany();

  const visibleMenuItems = menuItems.filter((item) => {
    if (item.topamaisOnly && !isMasterCompany) {
      return false;
    }
    return true;
  });

  const handleNavigate = async (href?: string) => {
    if (href === "/propostas") {
      await refreshProposalsCache(queryClient);
      router.push("/propostas");
      router.refresh();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClose?.();
            }
          }}
          role="button"
          tabIndex={0}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-40 h-screen w-64 border-r border-gray-200 bg-[#acfcf3] transition-transform duration-300",
          isOpen ? "translate-x-0" : "-translate-x-full",
          // Em telas grandes, o sidebar pode ser colapsado
          "md:transition-transform lg:transition-transform",
        )}
        aria-label="Menu principal"
        aria-hidden={!isOpen}
      >
        <nav className="flex h-full flex-col p-4">
          {/* Logo */}
          <div className="mb-8 px-2 py-4">
            <div className="flex items-center justify-center">
              <Logo width={140} height={46} />
            </div>
          </div>

          {/* Menu Items */}
          <ul
            className="flex-1 space-y-1"
            role="menubar"
            aria-label="Navegação principal"
          >
            {visibleMenuItems.map((item, index) => (
              <Fragment key={item.label}>
                <MenuItemWithPermission
                  item={item}
                  onClose={onClose}
                  onNavigate={handleNavigate}
                />
                {/* Divisor entre grupos principais */}
                {index === 2 && (
                  <li className="my-2" aria-hidden="true">
                    <div className="h-px bg-white/30" />
                  </li>
                )}
              </Fragment>
            ))}
          </ul>
        </nav>
      </aside>
    </>
  );
}
